import React, { ReactNode, ButtonHTMLAttributes } from 'react';
import { useFocusOutlineState } from 'app/containers/state/FocusOutlineState';
import { useCombineCallbackRefs } from 'app/utils/react';

export interface IPropsUnstyled extends ButtonHTMLAttributes<HTMLButtonElement> {
  type?: 'button' | 'submit' | 'reset'; // Defaults to type="button"
  children?: ReactNode;
  getRef?: (node: HTMLButtonElement | null) => void;
}

export interface IProps extends IPropsUnstyled {
  // Don't allow overriding style prop, because this will clobber <FocusOutlineState> behavior.
  style?: never;
}

/**
 * Button that handles its focus outline in a smart way (only showing focus
 * outline when interacting with the keyboard and not with the mouse).
 */
export default function BaseButton({ getRef, ...rest }: IProps) {
  const [showOutline, focusableRef] = useFocusOutlineState();
  const combinedRef = useCombineCallbackRefs([focusableRef, getRef]);

  return (
    <BaseButtonUnstyled
      {...rest}
      getRef={combinedRef}
      style={{ outline: showOutline ? undefined : 'none' }}
    />
  );
}

/**
 * Handles certain basic cases:
 *
 * - Defaults to type="button"
 * - Uses aria-disabled instead of disabled so buttons are discoverable with tab
 */
export const BaseButtonUnstyled: React.FunctionComponent<IPropsUnstyled> = ({
  children,
  getRef,
  type = 'button',
  disabled,
  onClick,
  ...rest
}) => (
  // eslint-disable-next-line react/forbid-elements
  <button
    {...rest}
    disabled={false} // disabling buttons is bad for accessibility (makes them impossible to discover)
    aria-disabled={disabled}
    type={!disabled ? type : 'button'}
    onClick={disabled ? undefined : onClick}
    data-submit-btn={type === 'submit' ? true : undefined}
    ref={getRef}
  >
    {children}
  </button>
);
